// userProfile.js — single read of all user-identity facts.
//
// Reads from three localStorage keys (the new "About me" Settings panel)
// plus the existing ghidhaProfile + calorieGoals + hayahPrefs, normalises
// into one structured object, and renders a stable "Profile block"
// string for inclusion in systemInstruction (so Gemini's implicit
// context caching can hit across turns in a conversation).
//
// New About-me keys (written by HayahSettings):
//   hayahUserBio        free-form text, capped at ~2000 chars
//   hayahUserRegion     defaults to "South Africa"
//   hayahFaithContext   "true" | "false", default "true"

const BIO_KEY    = "hayahUserBio";
const REGION_KEY = "hayahUserRegion";
const FAITH_KEY  = "hayahFaithContext";

const ACTIVITY_LABELS = {
  "1.2":   "Desk / sedentary",
  "1.375": "Light activity",
  "1.55":  "Active",
  "1.725": "Sport / hard training",
  "1.9":   "Athlete",
};

function safeJSON(key, fallback) {
  try { const v = JSON.parse(localStorage.getItem(key) || "null"); return v == null ? fallback : v; }
  catch { return fallback; }
}
function safeStr(key, fallback) {
  try { const v = localStorage.getItem(key); return v == null ? fallback : v; }
  catch { return fallback; }
}

export function getUserProfile() {
  const bio    = (safeStr(BIO_KEY, "") || "").trim().slice(0, 2000);
  const region = (safeStr(REGION_KEY, "") || "South Africa").trim();
  const faithRaw = safeStr(FAITH_KEY, "true");
  const faithContext = faithRaw === "false" ? false : true;

  const profile = safeJSON("ghidhaProfile", {}) || {};
  const goals   = safeJSON("calorieGoals", {}) || {};
  const prefs   = safeJSON("hayahPrefs", {}) || {};

  // BMR (Mifflin–St Jeor). Returns null if any input missing.
  const bmr = (() => {
    const age = parseInt(profile.age, 10);
    const ht  = parseFloat(profile.height);
    const wt  = parseFloat(profile.weight);
    if (!age || !ht || !wt || !profile.sex) return null;
    if (profile.sex === "M") return Math.round(10 * wt + 6.25 * ht - 5 * age + 5);
    return Math.round(10 * wt + 6.25 * ht - 5 * age - 161);
  })();
  const activityX = parseFloat(profile.activity) || null;
  const goalDelta = parseInt(profile.goal, 10) || 0;
  const tdee = (bmr && activityX) ? Math.round(bmr * activityX + goalDelta) : null;

  return {
    identity: {
      bio,
      region,
      faithContext,
    },
    body: {
      sex: profile.sex || null,
      age: parseInt(profile.age, 10) || null,
      heightCm: parseFloat(profile.height) || null,
      weightKg: parseFloat(profile.weight) || null,
      bmr,
      tdee,
    },
    training: {
      activity: profile.activity || null,
      activityLabel: ACTIVITY_LABELS[profile.activity] || null,
      goalDeltaKcal: goalDelta,
    },
    nutrition: {
      halalOnly: !!(prefs?.calories?.halalOnlyFilter),
      caloriesGoal: goals.calories || null,
      proteinGoal:  goals.protein  || null,
      carbsGoal:    goals.carbs    || null,
      fatGoal:      goals.fat      || null,
      fiberGoal:    goals.fiber    || null,
      weightTarget: goals.weight   || null,
      trainingDayOverride: goals.trainingEnabled ? goals.training : null,
    },
    preferences: {
      units: prefs?.units || { weight: "kg", energy: "kcal", time: "12h" },
      fastingProtocol: prefs?.fasting?.protocol || "16:8",
      fastingGoalHours: prefs?.fasting?.goalHours || 16,
    },
  };
}

// Renders a compact, stable profile block for inclusion in
// systemInstruction. Identical across turns of one conversation so
// implicit context caching can hit. Skips fields that are missing.
export function formatProfileForPrompt(p) {
  if (!p) p = getUserProfile();
  const lines = ["USER PROFILE:"];
  if (p.identity.bio) lines.push(`Bio: ${p.identity.bio}`);
  if (p.identity.region) lines.push(`Region: ${p.identity.region}`);
  const body = [];
  if (p.body.sex) body.push(p.body.sex === "M" ? "Male" : "Female");
  if (p.body.age) body.push(`${p.body.age}yo`);
  if (p.body.heightCm) body.push(`${p.body.heightCm}cm`);
  if (p.body.weightKg) body.push(`${p.body.weightKg}kg`);
  if (body.length) lines.push(`Body: ${body.join(" · ")}`);
  const energy = [];
  if (p.body.bmr)  energy.push(`BMR ~${p.body.bmr} kcal`);
  if (p.body.tdee) energy.push(`TDEE ~${p.body.tdee} kcal`);
  if (p.training.activityLabel) energy.push(p.training.activityLabel);
  if (energy.length) lines.push(`Energy: ${energy.join(" · ")}`);
  const goal = [];
  if (p.nutrition.caloriesGoal) goal.push(`${p.nutrition.caloriesGoal} kcal`);
  if (p.nutrition.proteinGoal)  goal.push(`${p.nutrition.proteinGoal}g P`);
  if (p.nutrition.carbsGoal)    goal.push(`${p.nutrition.carbsGoal}g C`);
  if (p.nutrition.fatGoal)      goal.push(`${p.nutrition.fatGoal}g F`);
  if (p.nutrition.weightTarget) goal.push(`target ${p.nutrition.weightTarget}kg`);
  if (goal.length) lines.push(`Goals: ${goal.join(" · ")}`);
  if (p.nutrition.trainingDayOverride) {
    const t = p.nutrition.trainingDayOverride;
    lines.push(`Training-day macros: ${t.calories} kcal · ${t.protein}g P · ${t.carbs}g C · ${t.fat}g F`);
  }
  if (p.preferences.fastingProtocol) {
    lines.push(`Fasting protocol: ${p.preferences.fastingProtocol} (${p.preferences.fastingGoalHours}h goal)`);
  }
  if (p.nutrition.halalOnly) lines.push("Halal-only foods: yes");
  return lines.join("\n");
}
